import { Component, OnInit, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '../shared/common.service';
import { ToastService } from '../shared/toast.service';
import { LoaderService } from '../shared/loader.service';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

interface InstallmentPlan {
  id: string;
  label: string;
  duration: string;
  price: number;
  totalInstallments: number;
}

interface DiscountCampaign {
  _id?: string;
  campaignName: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  startAt: string;
  endAt: string;
  studentLimit: number;
  usedCount: number;
  status: 'active' | 'inactive';
}

interface PackagePricing {
  originalPrice: number;
  finalPrice: number;
  discountAmount: number;
  isDiscountApplied: boolean;
  campaign: DiscountCampaign | null;
}

@Component({
  selector: 'app-billing-page',
  templateUrl: './billing-page.component.html',
  styleUrls: ['./billing-page.component.scss']
})
export class BillingPageComponent implements OnInit {
  billingForm: FormGroup;

  packageId: string | null = null;
  packageName: string | null = null;
  

  // old field kept for compatibility
  packagePrice: number | string | null = null;
  

  // ✅ new pricing fields
  originalPackagePrice: number | null = null;
  finalPackagePrice: number | null = null;
  discountAmount: number = 0;
  isDiscountApplied = false;
  activeDiscountCampaign: DiscountCampaign | null = null;

  packageSubtitle: string | null = null;
  packageBullets: string[] = [];

  selectedPlan: string = 'full';
  installmentPlans: InstallmentPlan[] = [];
  showInstallmentTabs = false;
  isLoading = false;

  showCountryDropdown = false;
  filteredCountries: string[] = [];

  countries: string[] = [
    'Afghanistan',
    'Albania',
    'Algeria',
    'Andorra',
    'Angola',
    'Antigua and Barbuda',
    'Argentina',
    'Armenia',
    'Australia',
    'Austria',
    'Azerbaijan',
    'Bahamas',
    'Bahrain',
    'Bangladesh',
    'Barbados',
    'Belarus',
    'Belgium',
    'Belize',
    'Benin',
    'Bhutan',
    'Bolivia',
    'Bosnia and Herzegovina',
    'Botswana',
    'Brazil',
    'Brunei',
    'Bulgaria',
    'Burkina Faso',
    'Burundi',
    'Cabo Verde',
    'Cambodia',
    'Cameroon',
    'Canada',
    'Central African Republic',
    'Chad',
    'Chile',
    'China',
    'Colombia',
    'Comoros',
    'Congo',
    'Costa Rica',
    'Croatia',
    'Cuba',
    'Cyprus',
    'Czech Republic',
    'Democratic Republic of the Congo',
    'Denmark',
    'Djibouti',
    'Dominica',
    'Dominican Republic',
    'Ecuador',
    'Egypt',
    'El Salvador',
    'Equatorial Guinea',
    'Eritrea',
    'Estonia',
    'Eswatini',
    'Ethiopia',
    'Fiji',
    'Finland',
    'France',
    'Gabon',
    'Gambia',
    'Georgia',
    'Germany',
    'Ghana',
    'Greece',
    'Grenada',
    'Guatemala',
    'Guinea',
    'Guinea-Bissau',
    'Guyana',
    'Haiti',
    'Honduras',
    'Hungary',
    'Iceland',
    'India',
    'Indonesia',
    'Iran',
    'Iraq',
    'Ireland',
    'Israel',
    'Italy',
    'Jamaica',
    'Japan',
    'Jordan',
    'Kazakhstan',
    'Kenya',
    'Kiribati',
    'Kuwait',
    'Kyrgyzstan',
    'Laos',
    'Latvia',
    'Lebanon',
    'Lesotho',
    'Liberia',
    'Libya',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Madagascar',
    'Malawi',
    'Malaysia',
    'Maldives',
    'Mali',
    'Malta',
    'Marshall Islands',
    'Mauritania',
    'Mauritius',
    'Mexico',
    'Micronesia',
    'Moldova',
    'Monaco',
    'Mongolia',
    'Montenegro',
    'Morocco',
    'Mozambique',
    'Myanmar',
    'Namibia',
    'Nauru',
    'Nepal',
    'Netherlands',
    'New Zealand',
    'Nicaragua',
    'Niger',
    'Nigeria',
    'North Korea',
    'North Macedonia',
    'Norway',
    'Oman',
    'Pakistan',
    'Palau',
    'Palestine',
    'Panama',
    'Papua New Guinea',
    'Paraguay',
    'Peru',
    'Philippines',
    'Poland',
    'Portugal',
    'Qatar',
    'Romania',
    'Russia',
    'Rwanda',
    'Saint Kitts and Nevis',
    'Saint Lucia',
    'Saint Vincent and the Grenadines',
    'Samoa',
    'San Marino',
    'Sao Tome and Principe',
    'Saudi Arabia',
    'Senegal',
    'Serbia',
    'Seychelles',
    'Sierra Leone',
    'Singapore',
    'Slovakia',
    'Slovenia',
    'Solomon Islands',
    'Somalia',
    'South Africa',
    'South Korea',
    'South Sudan',
    'Spain',
    'Sri Lanka',
    'Sudan',
    'Suriname',
    'Sweden',
    'Switzerland',
    'Syria',
    'Taiwan',
    'Tajikistan',
    'Tanzania',
    'Thailand',
    'Timor-Leste',
    'Togo',
    'Tonga',
    'Trinidad and Tobago',
    'Tunisia',
    'Turkey',
    'Turkmenistan',
    'Tuvalu',
    'Uganda',
    'Ukraine',
    'United Arab Emirates',
    'United Kingdom',
    'United States',
    'Uruguay',
    'Uzbekistan',
    'Vanuatu',
    'Vatican City',
    'Venezuela',
    'Vietnam',
    'Yemen',
    'Zambia',
    'Zimbabwe'
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private commonService: CommonService,
    private toastService: ToastService,
    private loaderService: LoaderService
  ) {
    this.billingForm = this.fb.group({
      paymentMethod: ['stripe', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      company: [''],
      email: ['', [Validators.required, Validators.email]],
      country: ['', Validators.required],
      state: [''],
      zip: [''],
      notes: [''],
      referralCode: [''],
      agreeToTerms: [false, Validators.requiredTrue],
      useWallet: [false],
      couponCode: ['']
    });
  }

  ngOnInit(): void {
    this.filteredCountries = [...this.countries];

    this.route.queryParams.subscribe(params => {
      this.packageId = params['packageId'] || null;

      if (!this.packageId) {
        this.toastService.showError('Invalid package');
        this.router.navigate(['/programs']);
        return;
      }

      this.fetchPackageDetails();
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.country-select')) {
      this.showCountryDropdown = false;
    }
  }

  openCountryDropdown(): void {
    this.showCountryDropdown = true;
    this.filterCountries();
  }

  toggleCountryDropdown(): void {
    this.showCountryDropdown = !this.showCountryDropdown;
    if (this.showCountryDropdown) {
      this.filterCountries();
    }
  }

  filterCountries(): void {
    const value = (this.billingForm.get('country')?.value || '').toLowerCase().trim();

    if (!value) {
      this.filteredCountries = [...this.countries];
      return;
    }

    this.filteredCountries = this.countries.filter(country =>
      country.toLowerCase().includes(value)
    );
  }

  selectCountry(country: string): void {
    this.billingForm.get('country')?.setValue(country);
    this.billingForm.get('country')?.markAsTouched();
    this.showCountryDropdown = false;
    this.filteredCountries = [...this.countries];
  }

  fetchPackageDetails(): void {
    this.loaderService.show();

    this.commonService.getPackages().subscribe({
      next: (res: any) => {
        if (res?.status === 1 && Array.isArray(res.data)) {
          const pkg = res.data.find((p: any) => p._id === this.packageId);

          if (!pkg) {
            this.toastService.showError('Package not found');
            this.router.navigate(['/programs']);
            this.loaderService.hide();
            return;
          }

          this.packageName = pkg.name || 'Selected Package';

          // ✅ New pricing-safe handling
          const pricing: PackagePricing | null = pkg?.pricing || null;
          const rawPrice = typeof pkg?.price === 'number' ? pkg.price : null;

          this.originalPackagePrice =
            pricing && typeof pricing.originalPrice === 'number'
              ? pricing.originalPrice
              : rawPrice;

          this.finalPackagePrice =
            pricing && typeof pricing.finalPrice === 'number'
              ? pricing.finalPrice
              : rawPrice;

          this.discountAmount =
            pricing && typeof pricing.discountAmount === 'number'
              ? pricing.discountAmount
              : 0;

          this.isDiscountApplied = !!pricing?.isDiscountApplied;
          this.activeDiscountCampaign = pricing?.campaign || pkg?.activeDiscountCampaign || null;

          // keep old field for compatibility with existing methods/checks
          this.packagePrice =
            typeof this.finalPackagePrice === 'number'
              ? this.finalPackagePrice
              : 'Custom Pricing';

          this.packageSubtitle = pkg.learningPathSubtitle || pkg.period || 'One Time';
          this.packageBullets = Array.isArray(pkg.learningPathBullets) ? pkg.learningPathBullets : [];

          if (pkg.installmentExist === true) {
            this.loadInstallmentPlans();
          } else {
            this.setupFullPaymentOnly();
          }
        } else {
          this.toastService.showError('Failed to load package');
          this.loaderService.hide();
        }
      },
      error: () => {
        this.loaderService.hide();
        this.toastService.showError('Error loading package');
      }
    });
  }

  loadInstallmentPlans(): void {
    if (!this.packageId || typeof this.finalPackagePrice !== 'number') {
      this.setupFullPaymentOnly();
      return;
    }

    this.commonService.getInstallmentPlan(this.packageId).pipe(
      tap((response: any) => {
        if (response.status === 1 && Array.isArray(response.data)) {
          const activePlans = response.data
            .filter((p: any) => p.status === 'active')
            .map((p: any) => {
              const perInstallment = (this.finalPackagePrice as number) / p.totalInstallments;
              return {
                id: p._id,
                label: p.planName,
                duration: `${p.totalInstallments} Month${p.totalInstallments > 1 ? 's' : ''}`,
                price: +perInstallment.toFixed(2),
                totalInstallments: p.totalInstallments
              };
            });

          if (activePlans.length > 0) {
            this.installmentPlans = [
              {
                id: 'full',
                label: 'Pay in Full',
                duration: 'One Time',
                price: this.finalPackagePrice as number,
                totalInstallments: 1
              },
              ...activePlans
            ];
            this.showInstallmentTabs = true;
            this.selectedPlan = 'full';
          } else {
            this.setupFullPaymentOnly();
          }
        } else {
          this.setupFullPaymentOnly();
        }
      }),
      catchError((err) => {
        console.warn('Installment plans failed to load', err);
        this.setupFullPaymentOnly();
        return throwError(() => err);
      })
    ).subscribe({
      next: () => this.loaderService.hide(),
      error: () => this.loaderService.hide()
    });
  }

  private setupFullPaymentOnly(): void {
    this.installmentPlans = [
      {
        id: 'full',
        label: 'Pay in Full',
        duration: 'One Time',
        price: typeof this.finalPackagePrice === 'number' ? this.finalPackagePrice : 0,
        totalInstallments: 1
      }
    ];
    this.showInstallmentTabs = false;
    this.selectedPlan = 'full';
    this.loaderService.hide();
  }

  selectPlan(planId: string): void {
    this.selectedPlan = planId;
  }

  applyCoupon(): void {
    const coupon = this.billingForm.get('couponCode')?.value?.trim();
    if (coupon) {
      this.toastService.showSuccess(`Applying coupon: ${coupon}`);
    } else {
      this.toastService.showError('Please enter a coupon code');
    }
  }

  onSubmit(): void {
    if (this.billingForm.invalid || !this.packageId) {
      this.toastService.showError('Please fill all required fields');
      return;
    }

    if (!this.billingForm.get('agreeToTerms')?.value) {
  this.billingForm.get('agreeToTerms')?.markAsTouched();
  this.toastService.showError('You must agree to the Terms & Conditions to continue');
  return;
}

    if (this.packagePrice === 'Custom Pricing') {
      this.toastService.showError('Custom pricing requires contacting support');
      return;
    }

    this.isLoading = true;
    this.loaderService.show();

    const formValue = this.billingForm.value;
    const payload: any = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      packageId: this.packageId,
      fullPayment: this.selectedPlan === 'full'
    };

    if (this.selectedPlan !== 'full') {
      payload.installmentPlanId = this.selectedPlan;
    }

    if (formValue.referralCode?.trim()) {
      payload.ref = formValue.referralCode.trim();
    }

    this.commonService.createCheckoutSession(payload).pipe(
      tap((response: any) => {
        if (response.status === 1 && response.url) {
          window.location.href = response.url;
        } else {
          throw new Error('Invalid checkout response');
        }
      }),
      catchError((error) => {
        const apiMessage =
          error?.error?.message ||
          error?.message ||
          'Payment failed. Please try again.';

        this.toastService.showError(apiMessage);
        this.isLoading = false;
        this.loaderService.hide();
        return throwError(() => error);
      })
    ).subscribe({
      next: () => {
        this.isLoading = false;
        this.loaderService.hide();
      },
      error: () => {
        this.isLoading = false;
        this.loaderService.hide();
      }
    });
  }

  getPlanDuration(): string {
    const plan = this.installmentPlans.find(p => p.id === this.selectedPlan);
    return plan ? plan.duration : 'One Time';
  }

  getTotalPrice(): string {
    if (this.packagePrice === 'Custom Pricing') return 'Custom Pricing';

    const plan = this.installmentPlans.find(p => p.id === this.selectedPlan);
    if (!plan) return '$0';

    const formattedPrice = plan.price.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    return this.selectedPlan === 'full'
      ? `$${formattedPrice}`
      : `$${formattedPrice}/mo × ${plan.totalInstallments}`;
  }

  getDisplayTotal(): string {
    if (this.packagePrice === 'Custom Pricing') return 'Custom Pricing';

    const plan = this.installmentPlans.find(p => p.id === this.selectedPlan);
    if (!plan) return '$0';

    if (this.selectedPlan === 'full') {
      return `$${this.formatMainPrice(plan.price)}`;
    }

    return `$${this.formatMainPrice(plan.price)}/mo`;
  }

  formatMainPrice(value: any): string {
    const num = Number(value || 0);
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  // ✅ helpers for discount display
  getOriginalPriceDisplay(): string {
    if (typeof this.originalPackagePrice !== 'number') return '';
    return `$${this.formatMainPrice(this.originalPackagePrice)}`;
  }

  getFinalPriceDisplay(): string {
    if (typeof this.finalPackagePrice !== 'number') return '';
    return `$${this.formatMainPrice(this.finalPackagePrice)}`;
  }

  getDiscountBadge(): string {
    if (!this.isDiscountApplied || !this.activeDiscountCampaign) return '';

    if (this.activeDiscountCampaign.discountType === 'percentage') {
      return `${this.activeDiscountCampaign.discountValue}% OFF`;
    }

    return `$${this.formatMainPrice(this.activeDiscountCampaign.discountValue)} OFF`;
  }

  getSavingsDisplay(): string {
    if (!this.discountAmount) return '';
    return `$${this.formatMainPrice(this.discountAmount)}`;
  }

  getDisplayPackageName(): string {
  if (this.activeDiscountCampaign?.campaignName) {
    return `${this.packageName} (${this.activeDiscountCampaign.campaignName})`;
  }
  return this.packageName || 'Selected Package';
}
}